import React from 'react';
import { AlertTriangle, Clock, XCircle, TrendingUp } from 'lucide-react';

interface AlertSummary {
  alert_type: string;
  alert_count: number;
  alert_severity: string;
  campaign_ids: string[];
}

interface AlertDashboardProps {
  alerts: AlertSummary[];
  onAlertClick?: (alertType: string, campaignIds: string[]) => void;
}

export function AlertDashboard({ alerts, onAlertClick }: AlertDashboardProps) {
  if (!alerts || alerts.length === 0 || alerts.every(a => a.alert_count === 0)) {
    return null;
  }

  const getAlertInfo = (alertType: string) => {
    switch (alertType) {
      case 'failed':
        return {
          icon: XCircle,
          title: 'Mislukte Campaigns',
          description: 'Campaigns met status "failed"',
          color: 'red'
        };
      case 'high_failure_rate':
        return {
          icon: TrendingUp,
          title: 'Hoge Faalpercentage',
          description: 'Campaigns met >5% mislukte emails',
          color: 'red'
        };
      case 'late_started':
        return {
          icon: Clock,
          title: 'Te Laat',
          description: 'Ingepland maar nog niet verzonden',
          color: 'yellow'
        };
      case 'stuck_sending':
        return {
          icon: AlertTriangle,
          title: 'Vastgelopen Verzending',
          description: 'Campaigns langer dan 5 min aan het verzenden',
          color: 'yellow'
        };
      default:
        return null;
    }
  };

  const visibleAlerts = alerts.filter(a => a.alert_count > 0);

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border-2 border-red-200 rounded-lg p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        <h2 className="text-lg font-semibold text-gray-900">Problemen Gedetecteerd</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {visibleAlerts.map((alert) => {
          const info = getAlertInfo(alert.alert_type);
          if (!info) return null;

          const Icon = info.icon;
          const bgColor = info.color === 'red' ? 'bg-red-50' : 'bg-yellow-50';
          const borderColor = info.color === 'red' ? 'border-red-200' : 'border-yellow-200';
          const textColor = info.color === 'red' ? 'text-red-900' : 'text-yellow-900';
          const iconColor = info.color === 'red' ? 'text-red-600' : 'text-yellow-600';
          const hoverBg = info.color === 'red' ? 'hover:bg-red-100' : 'hover:bg-yellow-100';

          return (
            <button
              key={alert.alert_type}
              onClick={() => onAlertClick?.(alert.alert_type, alert.campaign_ids)}
              className={`${bgColor} border ${borderColor} rounded-lg p-4 text-left transition-all ${hoverBg} cursor-pointer`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`font-semibold ${textColor} text-sm`}>
                      {info.title}
                    </h3>
                    <span className={`text-2xl font-bold ${textColor}`}>
                      {alert.alert_count}
                    </span>
                  </div>
                  <p className={`text-xs ${textColor} opacity-80`}>
                    {info.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
